import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  TiptapBubbleMenuDirective,
  TiptapEditorDirective,
  TiptapFloatingMenuDirective,
} from 'ngx-tiptap';
import {
  type ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  TouchedChangeEvent,
  type ValidationErrors,
} from '@angular/forms';

import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TextAlign } from '@tiptap/extension-text-align';
import { CharacterCount } from '@tiptap/extension-character-count';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Youtube } from '@tiptap/extension-youtube';
import type { Level } from '@tiptap/extension-heading';
import { TextStyleKit } from '@tiptap/extension-text-style';

import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { CustomMatSvgIconDirective } from '@pct-ng/icon';
import type { RichTextFeature } from '../type/rich-text-feature';
import { ALL_RICH_TEXT_FEATURES } from '../type/rich-text-feature';
import { provideI18nScope } from '@pct-ng/transloco';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatTooltip } from '@angular/material/tooltip';
import { I18nProviderInjectionToken } from '../i18n/i18n.scope';
import type { RichTextMenuMode } from '../type/rich-text-menu-mode.type';
import type { ControlValueAccessorOnChange, ControlValueAccessorOnTouched } from '@pct-ng/form';
import { ControlErrorComponent } from '@pct-ng/form';
import { requiredIgnoreHtmlValidator } from '../validators/required-ignore-html.validator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { IsNilPipe } from '@pct-ng/common/pipe';

@Component({
  selector: 'pct-feature-rich-text-tip-tap-pct-ng-feature-rich-text-tip-tap',
  imports: [
    CommonModule,
    TiptapEditorDirective,
    ReactiveFormsModule,
    MatIconButton,
    MatIcon,
    TiptapBubbleMenuDirective,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    CustomMatSvgIconDirective,
    TranslocoDirective,
    MatTooltip,
    TiptapFloatingMenuDirective,
    ControlErrorComponent,
    IsNilPipe,
  ],
  templateUrl: './pct-ng-feature-rich-text-tip-tap.component.html',
  styleUrls: ['./pct-ng-feature-rich-text-tip-tap.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PctNgFeatureRichTextTipTapComponent),
      multi: true,
    },
    provideI18nScope(I18nProviderInjectionToken),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PctNgFeatureRichTextTipTapComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  readonly #destroyRef = inject(DestroyRef);
  protected readonly i18nScope = inject<string>(I18nProviderInjectionToken);

  readonly submitted = input(false);
  readonly required = input<boolean>(false);
  readonly errors = input<ValidationErrors | null>(null);
  readonly placeholder = input<string>();
  readonly overrideErrors = input<Record<string, string> | undefined>(undefined);
  readonly nameAttribute = input.required<string>();
  readonly maxCharacterCount = input<number | null>(null);
  readonly enabledFeatures = input<RichTextFeature[]>(ALL_RICH_TEXT_FEATURES);
  readonly menuMode = input<RichTextMenuMode>('fixed');

  protected readonly hasBold = computed(() => this.enabledFeatures().includes('bold'));
  protected readonly hasItalic = computed(() => this.enabledFeatures().includes('italic'));
  protected readonly hasUnderline = computed(() => this.enabledFeatures().includes('underline'));
  protected readonly hasStrike = computed(() => this.enabledFeatures().includes('strike'));
  protected readonly hasCode = computed(() => this.enabledFeatures().includes('code'));
  protected readonly hasBlockquote = computed(() => this.enabledFeatures().includes('blockquote'));
  protected readonly hasBulletList = computed(() => this.enabledFeatures().includes('bulletList'));
  protected readonly hasOrderedList = computed(() =>
    this.enabledFeatures().includes('orderedList'),
  );
  protected readonly hasHeading = computed(() => this.enabledFeatures().includes('heading'));
  protected readonly hasTextAlign = computed(() => this.enabledFeatures().includes('textAlign'));
  protected readonly hasTextStyle = computed(() => this.enabledFeatures().includes('textStyle'));
  protected readonly hasLink = computed(() => this.enabledFeatures().includes('link'));
  protected readonly hasYoutube = computed(() => this.enabledFeatures().includes('youtube'));

  protected readonly levels: Level[] = [1, 2, 3, 4, 5, 6];

  protected readonly editor = new Editor({
    extensions: [
      StarterKit,
      CharacterCount.configure({
        limit: this.maxCharacterCount(),
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Youtube.configure({ controls: false }),
      Placeholder.configure({
        placeholder: () => this.placeholder() || '',
      }),
      TextStyleKit,
    ],
  });

  private readonly disabled = signal(false);
  #onChange!: ControlValueAccessorOnChange<string | null>;
  #onTouched!: ControlValueAccessorOnTouched;

  protected readonly richTextControl = new FormControl<string | null>(null, { nonNullable: true });

  private readonly editorCharacterCount = signal(0);
  protected readonly characterCount = computed(() => this.editorCharacterCount());

  constructor() {
    effect(() => {
      if (this.disabled()) {
        this.richTextControl.disable({ emitEvent: false });
      } else {
        this.richTextControl.enable({ emitEvent: false });
      }
    });

    effect(() => {
      if (this.submitted()) {
        this.richTextControl.markAsTouched();
      } else {
        this.richTextControl.markAsUntouched();
      }
    });

    effect(() => this.richTextControl.setErrors(this.errors(), { emitEvent: false }));

    effect(() => {
      if (this.required()) {
        this.richTextControl.addValidators([requiredIgnoreHtmlValidator]);
      } else {
        this.richTextControl.removeValidators([requiredIgnoreHtmlValidator]);
      }
      this.richTextControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.richTextControl.events
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter(e => e instanceof TouchedChangeEvent && e.touched),
      )
      .subscribe(() => this.#onTouched());

    this.richTextControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(value => this.#onChange?.(value));

    this.editor.on('update', () => {
      this.editorCharacterCount.set(this.editor.storage.characterCount.characters());
    });
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  writeValue(richText: string | null): void {
    this.richTextControl.setValue(richText, { emitEvent: false });
  }

  protected setLink(): void {
    const previousUrl = this.editor.getAttributes('link')['href'];
    const url = window.prompt('URL:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    try {
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } catch (e) {
      console.error(e);
    }
  }

  protected unsetLink(): void {
    this.editor.chain().focus().unsetLink().run();
  }

  protected insertYoutube(): void {
    const url = prompt('YouTube URL:');

    if (url) {
      this.editor.commands.setYoutubeVideo({
        src: url,
      });
    }
  }
}
